import { useMemo } from 'react';
import { get } from 'lodash';

import { FIELD_PATHS, LIABILITIES_CLEARING_FROM_OTHER_SOURCE, LOAN_OPTION } from '@funnels/constants';
import { useFormValues } from '@funnels/hooks';
import { useAnalytics } from '@funnels/providers';
import { LiabilityEntityIndex, TrackingProps } from '@funnels/types';
import { run } from '@funnels/utils';

import { StringDisplay } from '../../Fields';
import UncontrolledDropdownField from '../../Fields/_uncontrolled/DropdownField';
import { EditableViewProps, StringReadOnlyViewProps } from '../types';

const OPTIONS = [
  { label: 'None', value: LOAN_OPTION.NONE },
  { label: 'Consolidate', value: LOAN_OPTION.CONSOLIDATE },
  { label: 'Clear from another source', value: LOAN_OPTION.CLEAR_FROM_OTHER_SOURCE },
];

const NEW_OPTIONS = [
  { label: 'None', value: LOAN_OPTION.NONE },
  { label: 'Consolidate', value: LOAN_OPTION.CONSOLIDATE },
  { label: 'Clear from another source', value: LOAN_OPTION.CLEAR_FROM_OTHER_SOURCE },
  { label: 'Increase', value: LOAN_OPTION.INCREASE },
];

interface LoanOptionEditableViewProps extends LiabilityEntityIndex, EditableViewProps {
  trackingProps?: TrackingProps;
  validationFailMessage?: string;
  label?: string;
  shouldAnchorList?: boolean;
}

export function LoanOptionEditableView(props: LoanOptionEditableViewProps) {
  const { tracking } = useAnalytics();
  const [formValues, setFormValues] = useFormValues();
  const { label, liabilityIndex, trackingProps = {}, isDisabled, isOptional, shouldAnchorList = false } = props;
  const index = liabilityIndex;
  const liability = get(formValues, FIELD_PATHS.LIABILITY(index));
  const isTopup = get(formValues, FIELD_PATHS.LIABILITY_TO_BE_TOPPED_UP(index));

  const { selectedOption } = useMemo(() => {
    const liabilities = { liabilities: [liability] };
    const clearFromThisLoan = get(liabilities, FIELD_PATHS.LIABILITY_CLEARING_FROM_THIS_LOAN(0));
    const clearFromOtherSource = get(liabilities, FIELD_PATHS.LIABILITY_CLEARING_FROM_OTHER_SOURCE(0));
    let selectedOption;

    if (isTopup && !clearFromThisLoan) {
      selectedOption = LOAN_OPTION.INCREASE;
      return { selectedOption };
    }

    if (clearFromThisLoan === true) {
      selectedOption = LOAN_OPTION.CONSOLIDATE;
    } else if (clearFromOtherSource === LIABILITIES_CLEARING_FROM_OTHER_SOURCE.PAYING_OUT) {
      selectedOption = LOAN_OPTION.CLEAR_FROM_OTHER_SOURCE;
    } else if (clearFromThisLoan === false && !clearFromOtherSource) {
      selectedOption = LOAN_OPTION.NONE;
    }
    return { selectedOption };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liability, formValues]);

  const handledropdownTracking = (value: string) => {
    const selection =
      OPTIONS.find((option) => {
        return option.value === value;
      })?.label || '';
    tracking?.dropdown({ title: trackingProps.title!, selection, ...trackingProps });
  };

  function handleChange(value: string) {
    switch (value) {
      case LOAN_OPTION.CONSOLIDATE:
        setFormValues(FIELD_PATHS.LIABILITY_CLEARING_FROM_THIS_LOAN(index), true);
        setFormValues(FIELD_PATHS.LIABILITY_CLEARING_FROM_OTHER_SOURCE(index), undefined);

        break;
      case LOAN_OPTION.CLEAR_FROM_OTHER_SOURCE:
        setFormValues(FIELD_PATHS.LIABILITY_CLEARING_FROM_THIS_LOAN(index), false);
        setFormValues(
          FIELD_PATHS.LIABILITY_CLEARING_FROM_OTHER_SOURCE(index),
          LIABILITIES_CLEARING_FROM_OTHER_SOURCE.PAYING_OUT
        );

        break;
      case LOAN_OPTION.INCREASE:
      case LOAN_OPTION.NONE:
        setFormValues(FIELD_PATHS.LIABILITY_CLEARING_FROM_THIS_LOAN(index), false);
        setFormValues(FIELD_PATHS.LIABILITY_CLEARING_FROM_OTHER_SOURCE(index), undefined);
    }
  }

  return (
    <UncontrolledDropdownField
      label={label}
      data-component="loan-option-editable-view"
      options={isTopup ? NEW_OPTIONS : OPTIONS}
      size="md"
      value={selectedOption}
      onChange={run(handleChange, handledropdownTracking)}
      isDisabled={isDisabled}
      isOptional={isOptional}
      isFullWidth
      shouldAnchorList={shouldAnchorList}
    />
  );
}

export const LoanOptionReadOnlyView = (props: StringReadOnlyViewProps) => <StringDisplay {...props} />;
